import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { GlobalToastTypeNames, ToastTypes } from 'types';
import {
  useStudentLmsBlockQuery,
  useIssueLmsBlockMutation,
  useLiftLmsBlockMutation,
  StudentLmsBlockDocument,
} from 'core/graphql';

import { useToasts } from '../Toast/useToasts';

interface IUseLmsAccessBlock {
  isBlocked: boolean;
  reason: string | null;
  overdueAmount: number | null;
  loading: boolean;
  /** Confirm a debtor block; optional reason recorded for the audit + banner. */
  block: (reason?: string) => Promise<boolean>;
  /** Restore access (sets manual override server-side). */
  unblock: (reason?: string) => Promise<boolean>;
}

type ErrList =
  | Array<{ message?: string | null; code?: string | null } | null>
  | null
  | undefined;

type TFn = (key: string) => string;

/** Known backend error codes → i18n keys under lmsBlock.errors. */
const ERROR_CODE_KEYS: Record<string, string> = {
  not_a_debtor: 'pages.studentDetail.lmsBlock.errors.notADebtor',
  does_not_exist: 'pages.studentDetail.lmsBlock.errors.doesNotExist',
};

const errText = (errors: ErrList, t: TFn): string | undefined =>
  errors
    ?.map((e) => {
      const key = e?.code ? ERROR_CODE_KEYS[e.code] : undefined;
      return key ? t(key) : e?.message || e?.code || null;
    })
    .filter(Boolean)
    .join('\n') || undefined;

/**
 * Read + toggle a student's LMS-access block (spec 0003). Detection is
 * server-side; this only confirms / lifts the block. Toasts on failure;
 * refetches the block so the header label flips. Server gates (roles, "student
 * not found") surface as customErrors.
 */
export const useLmsAccessBlock = (studentId?: string): IUseLmsAccessBlock => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  const { data, loading: queryLoading } = useStudentLmsBlockQuery({
    variables: { studentId: studentId || '' },
    skip: !studentId,
    fetchPolicy: 'cache-and-network',
  });

  const opts = useMemo(
    () => ({
      refetchQueries: studentId
        ? [{ query: StudentLmsBlockDocument, variables: { studentId } }]
        : [],
      awaitRefetchQueries: true,
    }),
    [studentId],
  );

  const [blockMut, blockState] = useIssueLmsBlockMutation(opts);
  const [unblockMut, unblockState] = useLiftLmsBlockMutation(opts);

  const notifyError = useCallback(
    (text?: string) => {
      addToast({
        data: {
          type: ToastTypes.error,
          title: t('pages.studentDetail.lmsBlock.failed'),
          text,
        },
        type: GlobalToastTypeNames.LmsAccessBlock,
      });
    },
    [addToast, t],
  );

  const notifySuccess = useCallback(
    (title: string) => {
      addToast({
        data: {
          type: ToastTypes.success,
          title,
        },
        type: GlobalToastTypeNames.LmsAccessBlock,
      });
    },
    [addToast],
  );

  const block = useCallback(
    async (reason?: string) => {
      if (!studentId) return false;
      try {
        const res = await blockMut({
          variables: { studentId, reason: reason || null },
        });
        const payload = res.data?.blockStudentLmsAccess;
        if (payload?.success) {
          notifySuccess(t('pages.studentDetail.lmsBlock.blocked'));
          return true;
        }
        notifyError(errText(payload?.customErrors, t));
        return false;
      } catch (e) {
        notifyError((e as Error).message);
        return false;
      }
    },
    [blockMut, studentId, notifyError, notifySuccess, t],
  );

  const unblock = useCallback(
    async (reason?: string) => {
      if (!studentId) return false;
      try {
        const res = await unblockMut({
          variables: { studentId, reason: reason || null },
        });
        const payload = res.data?.unblockStudentLmsAccess;
        if (payload?.success) {
          notifySuccess(t('pages.studentDetail.lmsBlock.unblocked'));
          return true;
        }
        notifyError(errText(payload?.customErrors, t));
        return false;
      } catch (e) {
        notifyError((e as Error).message);
        return false;
      }
    },
    [unblockMut, studentId, notifyError, notifySuccess, t],
  );

  const record = data?.studentAccessBlock;

  return {
    isBlocked: Boolean(record?.isBlocked),
    reason: record?.reason ?? null,
    overdueAmount: record?.overdueAmount ?? null,
    loading: queryLoading || blockState.loading || unblockState.loading,
    block,
    unblock,
  };
};
